import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';

import { Gavi } from '#components/mobile/travesia/Gavi';
import type { GaviEstado } from '#components/mobile/travesia/Gavi';
import { abrirEntradaAtras } from '#components/mobile/ui/atras';
import type { EntradaAtras } from '#components/mobile/ui/atras';
import { Boton } from '#components/mobile/ui/Boton';
import { Icono } from '#components/mobile/ui/Icono';
import { color, texto } from '#components/mobile/ui/tokens';
import { useDispatch } from '#redux';
import { signOut } from '#users/usersSlice';

import { formatearEspera } from './esperaEntrar';
import {
  cambiarPedirHuellaAlAbrir,
  comprobarHuellaEnEsteMovil,
  pedirHuellaAlAbrir,
} from './huella';
import { Enlace } from './PantallaCofre';
import { CIFRAS_PIN, comprobarPin, segundosEsperaPin } from './pin';
import { PuntosPin, Tecla, TecladoPin } from './TecladoPin';

/** Tras este rato en segundo plano, se vuelve a pedir la huella. */
export const MINUTOS_SIN_PEDIR = 5;

/**
 * «Pedir la huella al abrir» (apagado de serie): tapa la app hasta que la
 * persona pone la huella o su PIN. Se pide al abrir y al volver tras un rato
 * fuera; la huella salta sola, sin pulsar nada (Vadik, 20-oct).
 */
export function BloqueoHuella() {
  const [cerrado, setCerrado] = useState(() => pedirHuellaAlAbrir());

  useEffect(() => {
    let oculta: number | null = null;
    const alCambiar = () => {
      if (document.visibilityState === 'hidden') {
        oculta = Date.now();
      } else if (
        oculta !== null &&
        Date.now() - oculta > MINUTOS_SIN_PEDIR * 60_000 &&
        pedirHuellaAlAbrir()
      ) {
        setCerrado(true);
      }
    };
    document.addEventListener('visibilitychange', alCambiar);
    return () => document.removeEventListener('visibilitychange', alCambiar);
  }, []);

  // Con el bloqueo puesto, «atrás» no hace nada: antes cambiaba de
  // pantalla por detrás y, al abrir, la persona aparecía en otra.
  useEffect(() => {
    if (!cerrado) {
      return;
    }
    let entrada: EntradaAtras | null = null;
    const tragar = () => {
      entrada = abrirEntradaAtras(tragar);
    };
    tragar();
    return () => entrada?.quitar();
  }, [cerrado]);

  if (!cerrado) {
    return null;
  }
  // En el body, para quedar encima de todo (también de la barra de abajo).
  return createPortal(
    <PantallaBloqueo onAbierta={() => setCerrado(false)} />,
    document.body,
  );
}

function PantallaBloqueo({ onAbierta }: { onAbierta: () => void }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [pin, setPin] = useState('');
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [gavi, setGavi] = useState<GaviEstado>('reposo');
  const [sacude, setSacude] = useState(0);
  const [espera, setEspera] = useState(() => segundosEsperaPin());
  const [pidiendo, setPidiendo] = useState(false);
  const [olvido, setOlvido] = useState(false);
  const yaPedida = useRef(false);

  async function pedirHuella() {
    if (pidiendo) {
      return;
    }
    setPidiendo(true);
    const res = await comprobarHuellaEnEsteMovil();
    setPidiendo(false);
    if (res === 'ok') {
      onAbierta();
    }
    // Si falla o se cancela, queda el teclado para el PIN.
  }

  // La huella salta sola al abrir, una vez (React la montaría dos en pruebas).
  useEffect(() => {
    if (!yaPedida.current && segundosEsperaPin() === 0) {
      yaPedida.current = true;
      void pedirHuella();
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- una vez al abrir
  }, []);

  const esperando = espera > 0;
  useEffect(() => {
    if (!esperando) {
      return;
    }
    const id = window.setInterval(() => setEspera(segundosEsperaPin()), 1000);
    return () => window.clearInterval(id);
  }, [esperando]);

  async function escribirCifra(cifra: string) {
    if (espera > 0 || pin.length >= CIFRAS_PIN) {
      return;
    }
    const nuevo = pin + cifra;
    setPin(nuevo);
    setMensaje(null);
    if (nuevo.length < CIFRAS_PIN) {
      return;
    }
    const res = await comprobarPin(nuevo);
    if (res === 'ok') {
      setGavi('aplaude');
      onAbierta();
      return;
    }
    setPin('');
    setSacude(s => s + 1);
    setGavi('eriza');
    const segundos = segundosEsperaPin();
    setEspera(segundos);
    if (segundos === 0) {
      setMensaje(t('Wrong PIN. Try again.'));
    }
  }

  return (
    <View
      data-testid="bloqueo-huella"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2147483000,
        backgroundColor: color.bg,
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        overflowY: 'auto',
      }}
    >
      {olvido ? (
        <OlvidoPin
          onVolver={() => setOlvido(false)}
          onCerrarSesion={() => {
            // Sin esto, al volver a entrar le pediría otra vez el PIN olvidado.
            cambiarPedirHuellaAlAbrir(false);
            void dispatch(signOut());
          }}
        />
      ) : (
        <View
          style={{
            width: '100%',
            maxWidth: 360,
            alignItems: 'center',
            gap: 22,
          }}
        >
          <Gavi
            estado={espera > 0 ? 'tapa' : gavi}
            clave={sacude}
            duracion={espera > 0 ? null : undefined}
            ancho={72}
          />
          <View style={{ gap: 6, alignItems: 'center' }}>
            <h1
              style={{
                ...texto.display,
                margin: 0,
                color: color.fg,
                textAlign: 'center',
              }}
            >
              <Trans>Welcome back aboard!</Trans>
            </h1>
            <Text
              style={{
                ...texto.cuerpo,
                fontSize: 15,
                color: color.fg2,
                textAlign: 'center',
              }}
            >
              <Trans>Use your fingerprint or your PIN to open Rumbo.</Trans>
            </Text>
          </View>
          <View key={sacude}>
            <PuntosPin escritas={pin.length} error={sacude > 0 && !pin} />
          </View>
          <View
            role="status"
            style={{ minHeight: 40, justifyContent: 'center' }}
          >
            {espera > 0 ? (
              <Text
                style={{
                  ...texto.secundario,
                  color: color.warn,
                  textAlign: 'center',
                }}
              >
                {t(
                  "Let's drop anchor for a moment! Too many tries in a row. Try again in {{tiempo}}.",
                  { tiempo: formatearEspera(espera) },
                )}
              </Text>
            ) : (
              mensaje && (
                <Text
                  style={{
                    ...texto.secundario,
                    color: color.bad,
                    textAlign: 'center',
                  }}
                >
                  {mensaje}
                </Text>
              )
            )}
          </View>
          <TecladoPin
            deshabilitado={espera > 0}
            onCifra={c => void escribirCifra(c)}
            onBorrar={() => setPin(p => p.slice(0, -1))}
            izquierda={
              <Tecla
                sinFondo
                onPress={() => void pedirHuella()}
                isDisabled={pidiendo}
                aria-label={t('Use fingerprint')}
              >
                <span style={{ color: color.accent, display: 'flex' }}>
                  <Icono nombre="huella" size={30} />
                </span>
              </Tecla>
            }
          />
          <View style={{ alignSelf: 'center' }}>
            <Enlace onPress={() => setOlvido(true)}>
              <Trans>Forgot your PIN?</Trans>
            </Enlace>
          </View>
        </View>
      )}
    </View>
  );
}

function OlvidoPin({
  onVolver,
  onCerrarSesion,
}: {
  onVolver: () => void;
  onCerrarSesion: () => void;
}) {
  return (
    <View
      style={{ width: '100%', maxWidth: 360, alignItems: 'center', gap: 20 }}
    >
      <Gavi estado="ladea" duracion={null} ancho={72} />
      <h1
        style={{
          ...texto.display,
          margin: 0,
          color: color.fg,
          textAlign: 'center',
        }}
      >
        <Trans>Forgot your PIN?</Trans>
      </h1>
      <Text
        style={{
          ...texto.cuerpo,
          fontSize: 15,
          color: color.fg2,
          textAlign: 'center',
        }}
      >
        <Trans>
          No problem. Sign out on this phone and sign in again with your
          password. Your data stays safe. Then you can choose a new PIN in
          Settings.
        </Trans>
      </Text>
      <Boton bloque onPress={onCerrarSesion}>
        <Trans>Sign out and use the password</Trans>
      </Boton>
      <View style={{ alignSelf: 'center' }}>
        <Enlace onPress={onVolver}>
          <Trans>Back</Trans>
        </Enlace>
      </View>
    </View>
  );
}
